import Experience from "@folio-2026/experience"
import { Menu } from "@folio-2026/menu"
import { EVENTS } from "@folio-2026/shared"
import {
  clamp,
  float,
  Fn,
  length,
  max,
  mix,
  remap,
  smoothstep,
  texture,
  time,
  uniform,
  uv,
  vec2,
  vec4,
} from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import Section from "../Section"

export default class SectionTransition {
  menu: Menu
  experience: Experience
  sizes: Experience["sizes"]
  renderer: Experience["renderer"]["instance"]
  debug: Experience["debug"]
  debugFolder?: FolderApi

  camera!: THREE.OrthographicCamera
  material!: THREE.NodeMaterial
  mesh!: THREE.Mesh

  sceneA: Section
  sceneB: Section

  transitionPass!: THREE.TextureNode

  postProcessing!: THREE.PostProcessing
  transitionProps = { progress: 0.5 }

  renderTarget!: THREE.RenderTarget

  texturePerlin!: THREE.Texture
  textureRandom!: THREE.Texture

  lastScrollPosition: number = 0
  lastScrollDirection: -1 | 0 | 1 = 1

  /** True when progress is strictly between 0 and 1 (both sections updating). */
  private wasTransitioning: string | null = null

  /** Progress from the previous frame, used to detect transition start. */
  private previousProgress: number = 0

  uniforms: {
    sampleA: THREE.TextureNode
    sampleB: THREE.TextureNode
    progress: THREE.UniformNode<number>
    scale: THREE.UniformNode<number>
    distortionScale: THREE.UniformNode<number>
    borderSize: THREE.UniformNode<number>
    patternHeight: THREE.UniformNode<number>
    cutMultiplier: THREE.UniformNode<number>
    rgbDisplacement: THREE.UniformNode<number>
    displacementY: THREE.UniformNode<number>
    scrollVelocity: THREE.UniformNode<number>
    velocityThreshold: THREE.UniformNode<number>
    scrollDisplacementFrequency: THREE.UniformNode<number>
    scrollDisplacementAmplitude: THREE.UniformNode<number>
    scrollDisplacementVerticalStretch: THREE.UniformNode<number>
    scrollDisplacementHorizontalStretch: THREE.UniformNode<number>
    scrollDirection: THREE.UniformNode<number>
  }

  constructor(sceneA: Section, sceneB: Section, menu: Menu) {
    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes
    this.renderer = this.experience.renderer.instance
    this.debug = this.experience.debug

    this.texturePerlin = this.experience.globalResources.perlin as THREE.Texture
    this.texturePerlin.wrapS = THREE.RepeatWrapping
    this.texturePerlin.wrapT = THREE.RepeatWrapping
    this.texturePerlin.minFilter = THREE.LinearFilter

    this.textureRandom = this.experience.globalResources.random as THREE.Texture
    this.textureRandom.wrapS = THREE.RepeatWrapping
    this.textureRandom.wrapT = THREE.RepeatWrapping
    this.textureRandom.minFilter = THREE.LinearFilter

    this.sceneA = sceneA
    this.sceneB = sceneB

    this.menu = menu

    this.uniforms = {
      sampleA: texture(this.sceneA.fbo.texture),
      sampleB: texture(this.sceneB.fbo.texture),
      progress: uniform(0),
      scale: uniform(10),
      distortionScale: uniform(0.5),
      borderSize: uniform(0.03),
      patternHeight: uniform(0.08),
      cutMultiplier: uniform(0.33),
      rgbDisplacement: uniform(0.02),
      displacementY: uniform(0.08),
      scrollVelocity: uniform(0),
      velocityThreshold: uniform(100),
      scrollDisplacementFrequency: uniform(2),
      scrollDisplacementAmplitude: uniform(0.8),
      scrollDisplacementVerticalStretch: uniform(0.2),
      scrollDisplacementHorizontalStretch: uniform(3.4),
      scrollDirection: uniform(1),
    }

    this.setDebug()
    this.setup()
  }

  setup() {
    const { width, height } = this.sizes

    this.camera = new THREE.OrthographicCamera(
      width / -200,
      width / 200,
      height / 200,
      height / -200,
      0.1,
      2,
    )
    this.camera.position.set(0, 0, 1)
    this.camera.lookAt(0, 0, 0)

    const geometry = new THREE.PlaneGeometry(
      this.sizes.width / 100,
      this.sizes.height / 100,
    )
    const material = new THREE.MeshBasicNodeMaterial()

    // Cache frequently used UV values at the top level
    const baseUV = uv()
    const centeredUV = baseUV.sub(vec2(0.5))
    const uvY = baseUV.y.oneMinus()
    const correctedUV = vec2(baseUV.x, uvY)

    const scaleUV = Fn(([uv, scale]: [any, any]) => {
      return uv.toVar().sub(vec2(0.5)).mul(scale).add(vec2(0.5))
    })

    // Calculate velocity factor: smoothly transition from 0 to 1 based on velocity relative to threshold
    // Use a wider range for more gradual transition
    // When velocity is below threshold, factor is 0 (show normal transition)
    // When velocity is above threshold * 15, factor is 1 (show maximum displacement)
    // The larger multiplier (15) creates a slower, more gradual appearance
    const velocityFactorRaw = smoothstep(
      this.uniforms.velocityThreshold,
      this.uniforms.velocityThreshold.mul(5),
      max(0, this.uniforms.scrollVelocity.abs()),
    )
    // Apply ease-out curve for smoother appearance: 1 - (1-t)^1.8
    // This creates a smooth ramp-up that accelerates quickly then decelerates
    // Makes the effect appear more smoothly when scrolling quickly
    const oneMinusT = velocityFactorRaw.oneMinus()
    const velocityFactor = oneMinusT.pow(1.5).oneMinus()

    const transitionEffect = Fn(([perlinValue]: [any]) => {
      // Use cached centeredUV instead of recalculating
      const distortedUV = scaleUV(
        correctedUV,
        vec2(
          float(1).sub(length(centeredUV.mul(this.uniforms.distortionScale))),
        ), // 1 plus the distance from the center multiplied by the distortion scale
      )

      // color each triangle randomly with a noise
      const perlinSample = texture(this.texturePerlin, correctedUV.mul(0.2))
      const z = perlinSample.r.mul(perlinSample.r)

      // const z = mx_noise_float(hexCoords.zw.mul(0.5).abs());

      // bounceTransition goes from 0 to 1 and back to 0
      // when the progress is 0.5, the bounceTransition is 1
      const bounceTransition = smoothstep(
        0,
        0.5,
        this.uniforms.progress.sub(0.5).abs(),
      ).oneMinus()

      const offset = float(0.2)
      // Use cached uvY instead of uv().y
      const blendCut = smoothstep(
        uvY.sub(offset),
        uv().y,
        remap(
          this.uniforms.progress.add(z.mul(0.2).mul(bounceTransition)),
          0,
          1,
          offset.mul(-1),
          offset.add(1),
        ), // we use remap to center the blendCut and void see the transition when the progress is 0 or 1
      )
      // it's the center slice. We use it to show the borders
      const merge = smoothstep(0, 0.5, blendCut.sub(0.5).abs()).oneMinus()

      // Create displacement mask that's taller than merge
      // displacementY controls the height of the displacement area
      // Use same calculation as merge but with displacementOffset to match direction (bottom to top)
      const displacementOffset = this.uniforms.displacementY
      const displacementBlendCut = smoothstep(
        uvY.sub(displacementOffset),
        uvY.add(displacementOffset),
        remap(
          this.uniforms.progress.add(z.mul(0.2).mul(bounceTransition)),
          0,
          1,
          displacementOffset.add(1),
          displacementOffset.mul(-1),
        ),
      )
      const displacementMask = smoothstep(
        0,
        0.5,
        displacementBlendCut.sub(0.5).abs(),
      ).oneMinus()

      // Mix distortedUV with correctedUV based on displacement mask
      // When displacementMask is close to 1 (taller transition area), use distortedUV
      // When displacementMask is close to 0, use correctedUV
      const finalUV = mix(correctedUV, distortedUV, displacementMask.mul(1.5))
      // Calculate progress threshold with offset, then remap to ensure pattern is visible
      // across the full range [0, 1] - this makes pattern immediately visible and keeps it visible
      const progressWithOffset = this.uniforms.progress.add(
        z.mul(this.uniforms.cutMultiplier).mul(bounceTransition),
      )
      // Remap progress to wider range to ensure pattern visibility at edges
      const progressThreshold = remap(
        progressWithOffset,
        0,
        1,
        float(-0.2), // Extend below 0 to show pattern immediately
        float(1.2), // Extend above 1 to keep pattern visible at end
      )
      // Use baseUV.y directly instead of cutUV (cutUV was just uvY.oneMinus() which equals baseUV.y)
      const cut = smoothstep(
        baseUV.y.sub(offset.div(2)),
        baseUV.y.add(offset.div(2)),
        progressThreshold,
      )

      // Calculate RGB displacement offset, multiplied by merge
      // Displacement direction follows merge (bottom to top)
      // Use positive Y to move upward in screen space (following merge from bottom to top)
      const displacementAmount = this.uniforms.rgbDisplacement.mul(merge)
      const displacementDir = vec2(0.75, 0.5)

      // Add perlin-based UV displacement on top of finalUV
      // Use perlinValue to create additional displacement, scaled by amplitude and merge
      const perlinDisplacement = vec2(
        perlinValue.sub(0.5).mul(2), // Remap from [0,1] to [-1,1]
        perlinValue.sub(0.5).mul(2),
      )
        .mul(this.uniforms.scrollDisplacementAmplitude)
        .mul(velocityFactor)

      // Apply perlin displacement to finalUV
      const finalUVWithPerlin = finalUV.add(perlinDisplacement)

      // Apply zoom-out effect based on scroll velocity
      // To zoom out (make sections appear smaller), scale UV coordinates by factor > 1
      // Higher velocity = more zoom out (larger scale factor)
      // Use velocityFactor to respect velocityThreshold - no zoom below threshold
      const maxZoomOut = float(0.3) // Maximum zoom-out (30% larger scale = 1.3)
      // Scale factor ranges from 1.0 (no zoom) to 1.0 + maxZoomOut (maximum zoom out)
      // velocityFactor is 0 below threshold, 1 at threshold*5, so zoom only activates above threshold
      const zoomScale = float(1).add(velocityFactor.mul(maxZoomOut))
      // Apply zoom using scaleUV function (centers UV, scales, then re-centers)
      const zoomedUV = scaleUV(finalUVWithPerlin, vec2(zoomScale))

      // Create offset UVs for each RGB channel using zoomedUV (which includes zoom, distortion and perlin displacement)
      // Swap add/sub to invert the displacement direction to follow merge
      const uvR = zoomedUV.sub(displacementDir.mul(displacementAmount))
      const uvG = zoomedUV // Green channel stays centered
      const uvB = zoomedUV.add(displacementDir.mul(displacementAmount))

      // Sample textures with offset UVs for each channel
      const sampleAR = texture(this.uniforms.sampleA, uvR)
      const sampleAG = texture(this.uniforms.sampleA, uvG)
      const sampleAB = texture(this.uniforms.sampleA, uvB)

      const sampleBR = texture(this.uniforms.sampleB, uvR)
      const sampleBG = texture(this.uniforms.sampleB, uvG)
      const sampleBB = texture(this.uniforms.sampleB, uvB)

      // Mix each channel separately
      const finalR = mix(sampleAR.r, sampleBR.r, cut)
      const finalG = mix(sampleAG.g, sampleBG.g, cut)
      const finalB = mix(sampleAB.b, sampleBB.b, cut)
      const finalA = mix(sampleAG.a, sampleBG.a, cut)

      // Combine RGB channels with displacement
      const textureMix = vec4(finalR, finalG, finalB, finalA)

      return textureMix
    })

    // Scroll displacement effect: apply perlin texture overlay based on velocity
    const scrollDisplacement = Fn(() => {
      // Cache scrollVelocity.abs() to avoid recalculating it twice
      const scrollVelocityAbs = this.uniforms.scrollVelocity.abs()

      // Calculate vertical translation based on scroll velocity and direction
      // Translation speed is proportional to scroll velocity
      // Direction determines if we translate up (-1) or down (1)
      const translationSpeed = scrollVelocityAbs.mul(0.001) // Scale factor to control translation speed
      const translation = time
        .mul(translationSpeed)
        .mul(this.uniforms.scrollDirection)

      // clamp velocity between 0 and 150 and map to 0 and 1
      const normalizedVelocityFactor = clamp(scrollVelocityAbs, 0, 150).div(150)

      // Apply hourglass effect: pinch the center horizontally
      // The effect is stronger at the center (y=0) and weaker at the edges
      // Use squared distance from center for smoother falloff
      // Use cached centeredUV from top level
      const verticalDistanceFromCenter = centeredUV.y.abs()
      // Create pinch factor: 1.0 at edges, less at center (proportional to velocity)
      // Higher velocity = stronger pinch
      const hourglassPinch = float(1).sub(
        verticalDistanceFromCenter
          .mul(verticalDistanceFromCenter)
          .mul(normalizedVelocityFactor.mul(0.5)),
      )

      // Apply hourglass effect to horizontal coordinate before stretching
      const hourglassUV = vec2(centeredUV.x.mul(hourglassPinch), centeredUV.y)

      // Stretch UV horizontally and vertically with centering (after hourglass effect)
      // Multiply by stretch, then add 0.5 back to center
      const stretchedUV = vec2(
        hourglassUV.x
          .mul(this.uniforms.scrollDisplacementHorizontalStretch)
          .add(0.5),
        hourglassUV.y
          .mul(this.uniforms.scrollDisplacementVerticalStretch)
          .add(0.5),
      )

      // Apply frequency scaling and vertical translation
      const perlinUV = vec2(
        stretchedUV.x.mul(
          this.uniforms.scrollDisplacementFrequency.mul(
            normalizedVelocityFactor,
          ),
        ),
        stretchedUV.y
          .mul(this.uniforms.scrollDisplacementFrequency)
          .add(translation),
      )

      const perlinSample = texture(this.texturePerlin, perlinUV)

      // Use perlin noise value (0-1 range from texture)
      const perlinValue = perlinSample.r

      return perlinValue
    })

    const displacedScene = scrollDisplacement()
    const sectionTransitionEffectResult = transitionEffect(displacedScene)

    const finalColor = this.menu.addMenuToColorNode(
      sectionTransitionEffectResult,
    )

    material.colorNode = finalColor

    const mesh = new THREE.Mesh(geometry, material)

    this.experience.scene.add(mesh)
    this.mesh = mesh
  }

  changeSceneA(scene: Section) {
    this.sceneA = scene
    this.uniforms.sampleA.value = scene.fbo.texture
  }

  changeSceneB(scene: Section) {
    this.sceneB = scene
    this.uniforms.sampleB.value = scene.fbo.texture
  }

  render(progress: number, params: { delta: number; elapsed: number }) {
    // Only update progress if it changed
    if (this.uniforms.progress.value !== progress) {
      this.uniforms.progress.value = progress
    }

    // Cache lenis reference to avoid repeated property access
    const lenis = this.experience.lenis

    // Update scroll velocity from lenis (only if changed)
    const velocity = lenis?.velocity ?? 0
    if (this.uniforms.scrollVelocity.value !== velocity) {
      this.uniforms.scrollVelocity.value = velocity
    }

    // Calculate direction from scroll position change for more reliable direction tracking
    const currentScroll = lenis?.animatedScroll ?? 0
    const scrollDelta = currentScroll - this.lastScrollPosition
    const SCROLL_THRESHOLD = 0.1

    let direction: -1 | 0 | 1
    if (Math.abs(scrollDelta) < SCROLL_THRESHOLD) {
      // No significant movement, use last known direction or scrollInfo direction
      const scrollInfoDirection = this.experience.scrollInfo?.direction ?? 0
      direction =
        scrollInfoDirection !== 0
          ? scrollInfoDirection
          : this.lastScrollDirection
    } else if (scrollDelta < 0) {
      // Scrolling up
      direction = -1
    } else {
      // Scrolling down
      direction = 1
    }

    // Update last known values
    this.lastScrollPosition = currentScroll
    this.lastScrollDirection = direction

    // Only update scrollDirection if it changed
    if (this.uniforms.scrollDirection.value !== direction) {
      this.uniforms.scrollDirection.value = direction
    }

    // Render textures
    if (progress === 0) {
      this.renderer.setRenderTarget(this.sceneA.fbo)
      this.sceneA.update(params)
    } else if (progress === 1) {
      this.renderer.setRenderTarget(this.sceneB.fbo)
      this.sceneB.update(params)
    } else {
      // console.log("A", this.sceneA.name, "B", this.sceneB.name);

      this.renderer.setRenderTarget(this.sceneA.fbo)
      this.sceneA.update(params)

      this.renderer.setRenderTarget(this.sceneB.fbo)
      this.sceneB.update(params)
    }

    // When transition starts (progress went from 0 or 1 to (0,1)), emit StartUpdate for the section that just started updating
    const isTransitioning = progress > 0 && progress < 1
    if (this.wasTransitioning && isTransitioning) {
      const currentSection = this.wasTransitioning
      const newSection = [this.sceneA, this.sceneB].filter(
        (section) => section.name !== currentSection,
      )[0]
      console.log("DIRECTION", direction)
      this.experience.trigger(EVENTS.Scroll.Section.StartUpdate, [
        { name: newSection.name, direction },
      ])
    }
    this.wasTransitioning = isTransitioning ? null : this.sceneA.name

    this.renderer.setRenderTarget(this.menu.fbo)
    this.menu.update(params)

    // Render on canvas
    this.renderer.setRenderTarget(null)
    this.renderer.render(this.experience.scene, this.camera)
  }

  resize() {
    if (!this.mesh) return

    // Use same source as renderer (Experience.resize → renderer.setSize(sizes)) so quad/camera
    // stay in sync when height decreases (canvas.clientHeight can lag on shrink).
    const { width, height } = this.sizes
    if (width <= 0 || height <= 0) return

    this.camera.left = width / -200
    this.camera.right = width / 200
    this.camera.top = height / 200
    this.camera.bottom = height / -200
    this.camera.updateProjectionMatrix()

    const oldGeometry = this.mesh.geometry
    this.mesh.geometry = new THREE.PlaneGeometry(width / 100, height / 100)
    oldGeometry.dispose()

    this.menu.resize()
  }

  setDebug() {
    if (this.debug.active && this.debug.ui) {
      this.debugFolder = this.debug.ui.addFolder({
        title: "🔮 Section Transition",
        expanded: false,
      })

      const settings = {
        scale: this.uniforms.scale.value,
        distortionScale: this.uniforms.distortionScale.value,
        borderSize: this.uniforms.borderSize.value,
        patternHeight: this.uniforms.patternHeight.value,
        cutMultiplier: this.uniforms.cutMultiplier.value,
        velocityThreshold: this.uniforms.velocityThreshold.value,
        scrollDisplacementFrequency:
          this.uniforms.scrollDisplacementFrequency.value,
        scrollDisplacementAmplitude:
          this.uniforms.scrollDisplacementAmplitude.value,
        scrollDisplacementVerticalStretch:
          this.uniforms.scrollDisplacementVerticalStretch.value,
        scrollDisplacementHorizontalStretch:
          this.uniforms.scrollDisplacementHorizontalStretch.value,
      }

      this.debugFolder
        .addBinding(settings, "scale", {
          min: 1,
          max: 100,
          step: 1,
        })
        .on("change", (ev) => {
          this.uniforms.scale.value = ev.value
        })

      this.debugFolder
        .addBinding(settings, "distortionScale", {
          min: -2,
          max: 2,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.uniforms.distortionScale.value = ev.value
        })

      this.debugFolder
        .addBinding(settings, "borderSize", {
          min: 0.01,
          max: 0.5,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.uniforms.borderSize.value = ev.value
        })

      this.debugFolder
        .addBinding(settings, "patternHeight", {
          min: 0.01,
          max: 0.3,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.uniforms.patternHeight.value = ev.value
        })

      this.debugFolder
        .addBinding(settings, "cutMultiplier", {
          min: 0,
          max: 0.5,
          step: 0.001,
        })
        .on("change", (ev) => {
          this.uniforms.cutMultiplier.value = ev.value
        })

      this.debugFolder
        .addBinding(settings, "velocityThreshold", {
          min: 0,
          max: 500,
          step: 10,
        })
        .on("change", (ev) => {
          this.uniforms.velocityThreshold.value = ev.value
        })

      this.debugFolder
        .addBinding(settings, "scrollDisplacementFrequency", {
          min: 0.1,
          max: 10,
          step: 0.1,
        })
        .on("change", (ev) => {
          this.uniforms.scrollDisplacementFrequency.value = ev.value
        })

      this.debugFolder
        .addBinding(settings, "scrollDisplacementAmplitude", {
          min: 0,
          max: 1,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.uniforms.scrollDisplacementAmplitude.value = ev.value
        })

      this.debugFolder
        .addBinding(settings, "scrollDisplacementVerticalStretch", {
          min: 0.1,
          max: 10,
          step: 0.1,
        })
        .on("change", (ev) => {
          this.uniforms.scrollDisplacementVerticalStretch.value = ev.value
        })

      this.debugFolder
        .addBinding(settings, "scrollDisplacementHorizontalStretch", {
          min: 0.1,
          max: 10,
          step: 0.1,
        })
        .on("change", (ev) => {
          this.uniforms.scrollDisplacementHorizontalStretch.value = ev.value
        })
    }
  }
}
